import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShoppingBag, Globe, Video } from "lucide-react";
import { HOMEPAGE } from "@/constants/testIds";
import PanchangWidget from "@/components/PanchangWidget";

const Home = () => {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden grain-overlay">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8">
            <p className="text-xs uppercase tracking-label text-secondary">Vedic astrology · Est. eternity</p>
            <h1 className="display-serif text-5xl sm:text-6xl lg:text-7xl">
              Read the stars.<br />
              <em className="italic text-primary">Honour</em> the ritual.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Personalised Vedic predictions written for you — and every sacred item your puja needs, curated from artisans across Bharat. Available in all 22 constitutional Indian languages.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="/predictions"
                data-testid={HOMEPAGE.heroCta}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold hover:bg-primary/90 luxe-btn"
              >
                Get my prediction <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/store"
                data-testid={HOMEPAGE.heroSecondaryCta}
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-foreground text-xs uppercase tracking-label font-bold hover:bg-foreground hover:text-background luxe-btn"
              >
                Browse samagri
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-border">
              <img
                src="https://images.unsplash.com/photo-1665003725647-3ae0f01140b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB0ZW1wbGUlMjBhcmNoaXRlY3R1cmUlMjBjYXJ2aW5nfGVufDB8fHx8MTc4NTA0NTE2MHww&ixlib=rb-4.1.0&q=85"
                alt="Sacred temple architecture"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p className="text-[10px] uppercase tracking-label opacity-80">Since Yugas</p>
                <p className="font-serif text-2xl leading-tight">Wisdom carved into the eternal.</p>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1729335511883-29eade10006b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHx6b2RpYWMlMjBhc3Ryb2xvZ3klMjBjaGFydCUyMHZpbnRhZ2V8ZW58MHx8fHwxNzg1MDQ1MTYwfDA&ixlib=rb-4.1.0&q=85"
              alt="Vintage zodiac chart"
              className="absolute -top-8 -right-8 w-32 h-32 object-cover border-4 border-background hidden lg:block"
            />
          </div>
        </div>
      </section>

      {/* Ornament */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="divider-ornament text-lg py-4">✦</div>
      </div>

      {/* Panchang widget */}
      <PanchangWidget />

      {/* Bento features */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
        <div className="grid md:grid-cols-6 gap-6">
          <Link
            to="/predictions"
            data-testid={HOMEPAGE.predictionsCta}
            className="md:col-span-4 group editorial-card p-10 hover:border-primary luxe-btn"
          >
            <Sparkles className="w-7 h-7 text-primary" strokeWidth={1.5} />
            <p className="text-[10px] uppercase tracking-label text-muted-foreground mt-6">01 · Predictions</p>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3">AI-crafted Vedic readings, in your language.</h2>
            <p className="text-sm text-muted-foreground mt-4 max-w-md">Daily horoscopes, deep Kundli analyses, and focused love/career/health guidance.</p>
            <span className="inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-label text-primary">Begin reading <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} /></span>
          </Link>

          <Link
            to="/store"
            data-testid={HOMEPAGE.storeCta}
            className="md:col-span-2 group editorial-card p-8 hover:border-primary luxe-btn"
          >
            <ShoppingBag className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <p className="text-[10px] uppercase tracking-label text-muted-foreground mt-6">02 · Store</p>
            <h2 className="font-serif text-2xl mt-3">Sacred goods, honestly sourced.</h2>
            <p className="text-sm text-muted-foreground mt-4">Diyas, agarbatti, idols, malas, ghee — from artisans.</p>
          </Link>

          <div className="md:col-span-2 editorial-card p-8 bg-primary text-primary-foreground">
            <Globe className="w-6 h-6" strokeWidth={1.5} />
            <p className="text-[10px] uppercase tracking-label opacity-80 mt-6">22 languages</p>
            <h2 className="font-serif text-2xl mt-3">हिन्दी · தமிழ் · বাংলা · తెలుగు · ਪੰਜਾਬੀ</h2>
            <p className="text-sm opacity-90 mt-4">Every Indian tongue of the Constitution.</p>
          </div>

          <div className="md:col-span-4 editorial-card p-10 relative overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1508133111629-be3f6e535a52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxnYW5lc2hhJTIwaWRvbCUyMGJyYXNzfGVufDB8fHx8MTc4NTA0NTE2MHww&ixlib=rb-4.1.0&q=85"
              alt="Brass Ganesha"
              className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-90 hidden sm:block"
            />
            <div className="relative max-w-sm">
              <p className="text-[10px] uppercase tracking-label text-secondary">03 · Ethos</p>
              <h2 className="font-serif text-3xl mt-3">Handmade by artisan families.</h2>
              <p className="text-sm text-muted-foreground mt-4">We buy directly from Moradabad brass workers, Kannauj perfumers and Nepal rudraksha collectors — never middlemen.</p>
            </div>
          </div>

          <Link to="/consult" className="md:col-span-6 group editorial-card p-10 flex flex-col sm:flex-row items-start sm:items-center gap-8 hover:border-primary luxe-btn">
            <Video className="w-8 h-8 text-primary shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-label text-secondary">04 · Consultations</p>
              <h2 className="font-serif text-3xl mt-2">Speak with a verified astrologer, one-on-one.</h2>
              <p className="text-sm text-muted-foreground mt-3">Private 30-min video sessions on Kundli, marriage, career and Vaastu — starting at $39.</p>
            </div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-label text-primary shrink-0">Book <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} /></span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
